'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FormData {
  organizationType: string;
  organizationName: string;
  contactPerson: string;
  position: string;
  email: string;
  phone: string;
  county: string;
  engagementType: string;
  projectScope: string;
  investmentRange: string;
  timeline: string;
  message: string;
}

interface GovernmentEngagementCTAProps {
  className?: string;
}

const GovernmentEngagementCTA = ({ className = '' }: GovernmentEngagementCTAProps) => {
  const [formData, setFormData] = useState<FormData>({
    organizationType: '',
    organizationName: '',
    contactPerson: '',
    position: '',
    email: '',
    phone: '',
    county: '',
    engagementType: '',
    projectScope: '',
    investmentRange: '',
    timeline: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const organizationTypes = [
    'National Government Ministry',
    'County Government',
    'State Corporation',
    'Regulatory Authority',
    'Development Agency',
    'Other Government Entity'
  ];

  const counties = [
    'Nairobi', 'Mombasa', 'Kisumu', 'Nakuru', 'Eldoret', 'Thika', 'Malindi', 'Kitale',
    'Garissa', 'Kakamega', 'Nyeri', 'Meru', 'Machakos', 'Kiambu', 'Kajiado', 'Other'
  ];

  const engagementTypes = [
    'Infrastructure Development Partnership',
    'Public-Private Partnership (PPP)',
    'Workforce Development Program',
    'Economic Zone Development',
    'Technology & Innovation Initiative',
    'Agricultural Value Chain Project',
    'Manufacturing Sector Investment',
    'Other Collaboration'
  ];

  const investmentRanges = [
    'Under KES 100M',
    'KES 100M - 500M',
    'KES 500M - 1B',
    'KES 1B - 5B',
    'Above KES 5B',
    'To Be Determined'
  ];

  const timelines = [
    'Immediate (0-3 months)',
    'Short-term (3-6 months)',
    'Medium-term (6-12 months)',
    'Long-term (12+ months)',
    'Multi-year Program'
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    await new Promise(resolve => setTimeout(resolve, 2000));

    setIsSubmitting(false);
    setSubmitStatus('success');

    setTimeout(() => {
      setSubmitStatus('idle');
      setFormData({
        organizationType: '',
        organizationName: '',
        contactPerson: '',
        position: '',
        email: '',
        phone: '',
        county: '',
        engagementType: '',
        projectScope: '',
        investmentRange: '',
        timeline: '',
        message: ''
      });
    }, 3000);
  };

  return (
    <section className={`py-20 bg-gradient-to-br from-primary/5 to-accent/5 ${className}`}>
      <div className="container mx-auto px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-headline text-3xl md:text-4xl font-semibold text-foreground mb-6">
              Government Partnership Inquiry
            </h2>
            <p className="font-body text-lg text-text-secondary leading-relaxed max-w-3xl mx-auto">
              We welcome engagement from government entities, regulatory authorities, and development agencies interested in exploring partnership opportunities for sustainable economic transformation.
            </p>
          </div>

          <div className="bg-card rounded-xl shadow-institutional border border-border overflow-hidden">
            <div className="grid grid-cols-1 lg:grid-cols-3">
              <div className="bg-gradient-to-br from-primary to-brand-forest p-8 text-white">
                <h3 className="font-headline text-xl font-semibold mb-6">Partnership Benefits</h3>
                <ul className="space-y-4">
                  {[
                    { icon: 'CurrencyDollarIcon', text: 'Patient capital with long-term commitment' },
                    { icon: 'ChartBarIcon', text: 'Measurable economic impact metrics' },
                    { icon: 'UserGroupIcon', text: 'Job creation and skills development' },
                    { icon: 'BuildingOffice2Icon', text: 'Infrastructure development support' },
                    { icon: 'DocumentCheckIcon', text: 'Full regulatory compliance' },
                    { icon: 'GlobeAltIcon', text: 'International best practices' }
                  ].map((benefit, index) => (
                    <li key={index} className="flex items-start space-x-3">
                      <Icon name={benefit.icon as any} size={20} variant="outline" className="text-accent flex-shrink-0 mt-0.5" />
                      <span className="font-body text-sm">{benefit.text}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-8 pt-8 border-t border-white/20">
                  <h4 className="font-headline text-lg font-semibold mb-4">Contact Information</h4>
                  <div className="space-y-3">
                    <div className="flex items-center space-x-3">
                      <Icon name="EnvelopeIcon" size={18} variant="outline" className="text-accent" />
                      <span className="font-body text-sm">partnerships@grandvilleholdings.com</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Icon name="PhoneIcon" size={18} variant="outline" className="text-accent" />
                      <span className="font-body text-sm">+254 20 123 4567</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Icon name="MapPinIcon" size={18} variant="outline" className="text-accent" />
                      <span className="font-body text-sm">Nairobi, Kenya</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="lg:col-span-2 p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="organizationType" className="block font-body text-sm font-medium text-foreground mb-2">
                        Organization Type *
                      </label>
                      <select
                        id="organizationType"
                        name="organizationType"
                        value={formData.organizationType}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-2.5 bg-background border border-input rounded-md font-body text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
                      >
                        <option value="">Select organization type</option>
                        {organizationTypes.map((type) => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label htmlFor="organizationName" className="block font-body text-sm font-medium text-foreground mb-2">
                        Organization Name *
                      </label>
                      <input
                        type="text"
                        id="organizationName"
                        name="organizationName"
                        value={formData.organizationName}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-2.5 bg-background border border-input rounded-md font-body text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
                        placeholder="Enter organization name"
                      />
                    </div>

                    <div>
                      <label htmlFor="contactPerson" className="block font-body text-sm font-medium text-foreground mb-2">
                        Contact Person *
                      </label>
                      <input
                        type="text"
                        id="contactPerson"
                        name="contactPerson"
                        value={formData.contactPerson}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-2.5 bg-background border border-input rounded-md font-body text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
                        placeholder="Full name"
                      />
                    </div>

                    <div>
                      <label htmlFor="position" className="block font-body text-sm font-medium text-foreground mb-2">
                        Position/Title *
                      </label>
                      <input
                        type="text"
                        id="position"
                        name="position"
                        value={formData.position}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-2.5 bg-background border border-input rounded-md font-body text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
                        placeholder="Your position"
                      />
                    </div>

                    <div>
                      <label htmlFor="email" className="block font-body text-sm font-medium text-foreground mb-2">
                        Official Email *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-2.5 bg-background border border-input rounded-md font-body text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
                        placeholder="email@organization.go.ke"
                      />
                    </div>

                    <div>
                      <label htmlFor="phone" className="block font-body text-sm font-medium text-foreground mb-2">
                        Phone Number *
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-2.5 bg-background border border-input rounded-md font-body text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
                        placeholder="+254 7XX XXX XXX"
                      />
                    </div>

                    <div>
                      <label htmlFor="county" className="block font-body text-sm font-medium text-foreground mb-2">
                        County/Region *
                      </label>
                      <select
                        id="county"
                        name="county"
                        value={formData.county}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-2.5 bg-background border border-input rounded-md font-body text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
                      >
                        <option value="">Select county</option>
                        {counties.map((county) => (
                          <option key={county} value={county}>{county}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label htmlFor="engagementType" className="block font-body text-sm font-medium text-foreground mb-2">
                        Engagement Type *
                      </label>
                      <select
                        id="engagementType"
                        name="engagementType"
                        value={formData.engagementType}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-2.5 bg-background border border-input rounded-md font-body text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
                      >
                        <option value="">Select engagement type</option>
                        {engagementTypes.map((type) => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label htmlFor="investmentRange" className="block font-body text-sm font-medium text-foreground mb-2">
                        Investment Range
                      </label>
                      <select
                        id="investmentRange"
                        name="investmentRange"
                        value={formData.investmentRange}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2.5 bg-background border border-input rounded-md font-body text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
                      >
                        <option value="">Select investment range</option>
                        {investmentRanges.map((range) => (
                          <option key={range} value={range}>{range}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label htmlFor="timeline" className="block font-body text-sm font-medium text-foreground mb-2">
                        Expected Timeline
                      </label>
                      <select
                        id="timeline"
                        name="timeline"
                        value={formData.timeline}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2.5 bg-background border border-input rounded-md font-body text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
                      >
                        <option value="">Select timeline</option>
                        {timelines.map((time) => (
                          <option key={time} value={time}>{time}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="projectScope" className="block font-body text-sm font-medium text-foreground mb-2">
                      Project Scope/Objectives *
                    </label>
                    <textarea
                      id="projectScope"
                      name="projectScope"
                      value={formData.projectScope}
                      onChange={handleInputChange}
                      required
                      rows={3}
                      className="w-full px-4 py-2.5 bg-background border border-input rounded-md font-body text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent resize-none"
                      placeholder="Brief description of project scope and objectives"
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="block font-body text-sm font-medium text-foreground mb-2">
                      Additional Information
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      rows={4}
                      className="w-full px-4 py-2.5 bg-background border border-input rounded-md font-body text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent resize-none"
                      placeholder="Any additional details or specific requirements"
                    />
                  </div>

                  <div className="flex items-center justify-between pt-4">
                    <p className="font-body text-xs text-muted-foreground">
                      * Required fields. We will respond within 2-3 business days.
                    </p>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="inline-flex items-center space-x-2 px-8 py-3 bg-secondary text-secondary-foreground font-body font-medium text-sm rounded-md hover:bg-secondary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300"
                    >
                      {isSubmitting ? (
                        <>
                          <Icon name="ArrowPathIcon" size={20} variant="outline" className="animate-spin" />
                          <span>Submitting...</span>
                        </>
                      ) : (
                        <>
                          <span>Submit Inquiry</span>
                          <Icon name="PaperAirplaneIcon" size={20} variant="outline" />
                        </>
                      )}
                    </button>
                  </div>

                  {submitStatus === 'success' && (
                    <div className="flex items-center space-x-3 bg-success/10 border border-success/20 rounded-md p-4">
                      <Icon name="CheckCircleIcon" size={24} variant="solid" className="text-success flex-shrink-0" />
                      <div>
                        <p className="font-body text-sm font-medium text-success">Inquiry submitted successfully!</p>
                        <p className="font-body text-xs text-success/80 mt-1">Our partnership team will contact you within 2-3 business days.</p>
                      </div>
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default GovernmentEngagementCTA;